// ═══════════════════════════════════════════════════════════════════════════════
// Settlement detail — title row three-dot menu (matches task list title menu UI)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';

export type SettlementDetailTitleMenuItem = {
  id: string;
  label: string;
  onSelect: () => void;
};

type SettlementDetailTitleMenuProps = {
  ariaLabel: string;
  items: SettlementDetailTitleMenuItem[];
};

export const SettlementDetailTitleMenu = ({ ariaLabel, items }: SettlementDetailTitleMenuProps) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  if (items.length === 0) {
    return null;
  }

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleItemSelect = (item: SettlementDetailTitleMenuItem) => {
    setOpen(false);
    item.onSelect();
  };

  return (
    <div className="relative shrink-0" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        tabIndex={0}
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200/80 bg-white text-gray-500 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={3}>
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="19" r="1" />
          </g>
        </svg>
      </button>
      {open ? (
        <div
          className="absolute right-0 top-9 z-50 min-w-[11rem] overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
          role="menu"
        >
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              tabIndex={0}
              className={`flex w-full items-center gap-2 px-4 py-3 text-left text-[13px] font-medium text-gray-900 hover:bg-gray-50 focus:outline-none dark:text-gray-100 dark:hover:bg-gray-800 ${
                index < items.length - 1 ? 'border-b border-gray-200/40 dark:border-gray-700' : ''
              }`}
              onClick={() => handleItemSelect(item)}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
